import React, { useEffect, useRef, useState } from "react";
import { Button } from "./ui";
import { Link, useNavigate } from "react-router-dom";
import logo from "../assets/logos/logo.png";
import { api } from "../services";
import { useTranslation } from "react-i18next";
import { saveFlashMessage } from "../utils/flashMessage";
import { usePermission } from "../hooks/usePermission";
import { getFirstAccessibleTenantPath } from "../config/routePermissions";
import { loadCachedAccessSummary } from "../services";
import AuthPageLayout from "./AuthPageLayout";

function Register() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [registerError, setRegisterError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("translation");
  const { refresh } = usePermission();
  const didRun = useRef(false);

  const validateForm = () => {
    const errors = {};
    if (!email) errors.email = `${t("Email address is required.")}`;
    if (!phone) errors.phone = `${t("Phone number is required.")}`;
    if (!password) errors.password = `${t("Password is required.")}`;
    if (!confirmPassword) {
      errors.confirmPassword = `${t("Confirm Password is required.")}`;
    }
    if (password && confirmPassword && password !== confirmPassword) {
      errors.confirmPassword = `${t("Passwords do not match.")}`;
    }
    if (!policyAccepted) {
      errors.policyAccepted = `${t("You must accept the Terms and Privacy Policy.")}`;
    }
    return errors;
  };

  const handleRegister = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setRegisterError(null);
    setLoading(true);
    try {
      await api.post("/register", {
        email,
        phone,
        password,
        user_type: "company",
      });
      navigate("/account-created", { state: { email } });
    } catch (err) {
      setRegisterError(
        err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || ""
      );
    } finally {
      setLoading(false);
    }
  };

  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp && payload.exp < currentTime;
    } catch {
      return true;
    }
  };

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;

    const token = localStorage.getItem("token");
    const adminToken = localStorage.getItem("adminToken");

    if (adminToken && !isTokenExpired(adminToken)) {
      saveFlashMessage(`${t("You are already logged in.")}`, "info");
      navigate("/admin/user-list", { replace: true });
    } else if (token && !isTokenExpired(token)) {
      saveFlashMessage(`${t("You are already logged in.")}`, "info");
      (async () => {
        const summary = (await refresh()) || loadCachedAccessSummary();
        navigate(getFirstAccessibleTenantPath(summary), { replace: true });
      })();
    }
  }, [navigate, refresh, t]);

  useEffect(() => {
    setFormErrors((current) =>
      Object.keys(current).length > 0 ? validateForm() : current
    );
  }, [i18n.language]);

  const error =
    registerError == null
      ? ""
      : registerError === "User already registered."
        ? t("User already registered.")
        : t(registerError, {
            defaultValue: t("An error occurred during registration. Please try again."),
          });

  const privacyUrl =
    i18n.language === "ar"
      ? "https://www.loops.sa/ar/privacy-policy"
      : "https://www.loops.sa/en/privacy-policy";
  const termsUrl =
    i18n.language === "ar"
      ? "https://www.loops.sa/ar/terms-of-use"
      : "https://www.loops.sa/en/terms-of-use";

  return (
    <AuthPageLayout>
      <div className="auth-page-card__logo">
        <img src={logo} alt="Loops" />
      </div>

      <form
        className="auth-register-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleRegister();
        }}
        noValidate
      >
        <div
          className={`auth-register-field${formErrors.email ? " auth-register-field--error" : ""}`}
        >
          <input
            id="register-email"
            type="email"
            autoComplete="email"
            placeholder={t("Email address")}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (formErrors.email) {
                setFormErrors((prev) => ({ ...prev, email: "" }));
              }
            }}
            aria-invalid={Boolean(formErrors.email)}
          />
          {formErrors.email ? (
            <p className="auth-register-field__error">{formErrors.email}</p>
          ) : null}
        </div>

        <div
          className={`auth-register-field${formErrors.phone ? " auth-register-field--error" : ""}`}
        >
          <input
            id="register-phone"
            type="tel"
            autoComplete="tel"
            placeholder={t("Phone Number")}
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              if (formErrors.phone) {
                setFormErrors((prev) => ({ ...prev, phone: "" }));
              }
            }}
            aria-invalid={Boolean(formErrors.phone)}
          />
          {formErrors.phone ? (
            <p className="auth-register-field__error">{formErrors.phone}</p>
          ) : null}
        </div>

        <div
          className={`auth-register-field${formErrors.password ? " auth-register-field--error" : ""}`}
        >
          <input
            id="register-password"
            type="password"
            autoComplete="new-password"
            placeholder={t("Password")}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (formErrors.password) {
                setFormErrors((prev) => ({ ...prev, password: "" }));
              }
            }}
            aria-invalid={Boolean(formErrors.password)}
          />
          {formErrors.password ? (
            <p className="auth-register-field__error">{formErrors.password}</p>
          ) : null}
        </div>

        <div
          className={`auth-register-field${
            formErrors.confirmPassword ? " auth-register-field--error" : ""
          }`}
        >
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            placeholder={t("Confirm Password")}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (formErrors.confirmPassword) {
                setFormErrors((prev) => ({ ...prev, confirmPassword: "" }));
              }
            }}
            aria-invalid={Boolean(formErrors.confirmPassword)}
          />
          {formErrors.confirmPassword ? (
            <p className="auth-register-field__error">{formErrors.confirmPassword}</p>
          ) : null}
        </div>

        <div className="auth-register-terms">
          <input
            type="checkbox"
            id="policy-checkbox"
            checked={policyAccepted}
            onChange={(e) => {
              setPolicyAccepted(e.target.checked);
              if (formErrors.policyAccepted) {
                setFormErrors((prev) => ({ ...prev, policyAccepted: "" }));
              }
            }}
          />
          <label
            className="auth-register-terms__label"
            htmlFor="policy-checkbox"
            onClick={() => window.open(privacyUrl, "_blank", "noopener,noreferrer")}
          >
            {t("Accept terms and conditions")}
          </label>
        </div>
        {formErrors.policyAccepted ? (
          <p className="auth-register-field__error">{formErrors.policyAccepted}</p>
        ) : null}

        {error ? <p className="auth-register-error">{error}</p> : null}

        <Button
          type="submit"
          color="primary"
          size="md"
          className="auth-register-submit"
          disabled={loading}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              {t("Registering")}...
            </>
          ) : (
            t("Register")
          )}
        </Button>
      </form>

      <p className="auth-register-login">
        <span>
          {t("Already have an account?")}{" "}
          <Link to="/?login=true">{t("Login here")}</Link>
        </span>
      </p>

      <div className="auth-register-footer">
        <button
          type="button"
          onClick={() => window.open(termsUrl, "_blank", "noopener,noreferrer")}
        >
          {t("Terms of use")}
        </button>
        <span className="auth-register-footer__sep" aria-hidden>
          |
        </span>
        <button
          type="button"
          onClick={() => window.open(privacyUrl, "_blank", "noopener,noreferrer")}
        >
          {t("Privacy policy")}
        </button>
      </div>
    </AuthPageLayout>
  );
}

export default Register;
